import React from 'react'
import { Loading, Text } from '@workwhile/ui'
import { formatInTimeZone } from 'date-fns-tz'
import { formatDistanceToNowStrict, isPast, isSameYear } from 'date-fns'
import { ShiftRoster } from 'api/shifts/getShiftRosterByShiftId'
import { useShiftRosterByShiftIdQuery } from 'queries/shifts/useShiftRosterByShiftIdQuery'

type Props = {
  shiftId: number
  timezone: string
}

const getFormattedTimeToDisplay = (
  date: Date | string,
  timezone: string
): string => {
  const isDateInCurrentYear = isSameYear(date, new Date())
  const format = isDateInCurrentYear
    ? 'MMM d, h:mm a z'
    : 'MMM d, yyyy, h:mm a z'
  const formattedDate = formatInTimeZone(date, timezone, format)

  const formattedDistance = formatDistanceToNowStrict(date, {
    addSuffix: true,
  })

  return `${formattedDate} (${formattedDistance})`
}

const getRosterTierSubtitle = (
  roster: ShiftRoster | undefined,
  timezone: string
): string | null => {
  if (!roster) {
    return null
  }

  if (roster.rosterHoldExpiresAt) {
    const verb = isPast(new Date(roster.rosterHoldExpiresAt))
      ? 'expired'
      : 'expires'
    return (
      `Roster hold ${verb} ` +
      getFormattedTimeToDisplay(roster.rosterHoldExpiresAt, timezone)
    )
  }

  return 'Permanent roster hold'
}

export const RosterTierSubtitle = ({ shiftId, timezone }: Props) => {
  const { data: roster, isLoading } = useShiftRosterByShiftIdQuery(
    shiftId,
    true
  )

  if (isLoading) {
    return <Loading size={8} />
  }

  const subtitle = getRosterTierSubtitle(roster, timezone)
  if (!subtitle) {
    return null
  }

  return (
    <Text color="lightText" fontSize={1}>
      {subtitle}
    </Text>
  )
}
